import { Award, Bell, ChevronLeft, Heart, Home, Menu, Sprout, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { characterAssets, functionAssets } from '../data/assets'
import { adapterLoaders, useAdapterData } from '../data/use-adapter-data'
import type { UserRole } from '../data/app-adapter'
import { managerRoles } from '../router/access'
import { useSession } from '../session/SessionProvider'
import type { PreviewState } from './StatePanels'

const primaryNav = [
  { to: '/home', label: '首頁', icon: Home },
  { to: '/growth', label: '任務', mark: '▣' },
  { to: '/garden', label: '家園', icon: Sprout },
  { to: '/thanks', label: '感謝', icon: Heart },
  { to: '/badges', label: '勳章', icon: Award },
]

const drawerItems = [
  { to: '/documents', label: '文件中心', description: 'SOP、規章與公告文件', image: functionAssets.documents },
  { to: '/knowledge', label: 'VASO 智庫', description: '查詢工作知識與常見問題', image: functionAssets.knowledge },
  { to: '/promotion', label: '升遷申請', description: '資格與申請狀態', image: functionAssets.promotion },
  { to: '/account', label: '帳號設定', description: '個人資料與帳號操作', image: functionAssets.account },
]

const roleOptions: Array<{ value: UserRole; label: string }> = [
  { value: 'employee', label: '員工' },
  { value: 'supervisor', label: '一般主管' },
  { value: 'manager_l1', label: '店長一級' },
  { value: 'manager_l2', label: '店長二級' },
  { value: 'area_supervisor', label: '督導' },
]

function Drawer({ open, onClose }: { open: boolean; onClose(): void }) {
  const { user, setPreviewRole } = useSession()

  useEffect(() => {
    if (!open) return
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    document.addEventListener('keydown', closeOnEscape)
    document.body.classList.add('drawer-open')
    return () => {
      document.removeEventListener('keydown', closeOnEscape)
      document.body.classList.remove('drawer-open')
    }
  }, [open, onClose])

  return (
    <div className={`drawer-layer ${open ? 'is-open' : ''}`} aria-hidden={!open}>
      <button className="drawer-backdrop" aria-label="關閉選單" onClick={onClose} />
      <aside className="drawer" aria-label="功能選單">
        <div className="drawer__head">
          <div><small>VASO HOME</small><h2>工作入口</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="關閉選單"><X /></button>
        </div>
        <div className="drawer__profile">
          <img src={characterAssets.male} alt="阿豪的 Q 版人物" />
          <div><strong>{user?.name ?? '尚未登入'}</strong><small>{user?.storeName} · {user?.roleLabel}</small></div>
        </div>
        <nav className="drawer__nav">
          {drawerItems.map((item) => (
            <Link key={item.to} to={item.to} onClick={onClose}>
              <img src={item.image} alt="" aria-hidden="true" />
              <span><strong>{item.label}</strong><small>{item.description}</small></span>
              <b aria-hidden="true">›</b>
            </Link>
          ))}
          {user && managerRoles.has(user.role) && (
            <Link to="/manager" onClick={onClose}>
              <img src={functionAssets.manager} alt="" aria-hidden="true" />
              <span><strong>管理者中心</strong><small>訓練、文件、權限與門市管理</small></span>
              <b aria-hidden="true">›</b>
            </Link>
          )}
        </nav>
        <label className="preview-role">
          <span>測試角色（只影響此 Preview）</span>
          <select value={user?.role ?? 'employee'} onChange={(event) => setPreviewRole(event.target.value as UserRole)}>
            {roleOptions.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
          </select>
        </label>
      </aside>
    </div>
  )
}

export function AppHeader({ title = 'VASO HOME' }: { title?: string }) {
  const [open, setOpen] = useState(false)
  const notifications = useAdapterData(adapterLoaders.notifications)
  const hasUnread = notifications.status === 'ready' && notifications.data.some((notice) => !notice.read)
  return (
    <>
      <header className="app-header">
        <button className="icon-button" onClick={() => setOpen(true)} aria-label="開啟功能選單"><Menu /></button>
        <Link to="/home" className="brand-title">{title}</Link>
        <Link to="/notifications" className="icon-button notification-link" aria-label="通知中心"><Bell />{hasUnread && <span />}</Link>
      </header>
      <Drawer open={open} onClose={() => setOpen(false)} />
    </>
  )
}

export function PageHeader({ title, backTo }: { title: string; backTo?: string }) {
  const navigate = useNavigate()
  return (
    <header className="page-header">
      <button className="page-header__back" onClick={() => backTo ? navigate(backTo) : navigate(-1)} aria-label="返回"><ChevronLeft /></button>
      <h1>{title}</h1>
      <span aria-hidden="true" />
    </header>
  )
}

export function BottomNav() {
  return (
    <nav className="bottom-nav" aria-label="主要導覽">
      {primaryNav.map(({ to, label, icon: Icon, mark }) => (
        <NavLink key={to} to={to} className={({ isActive }) => isActive ? 'is-active' : undefined}>
          {Icon ? <Icon /> : <b aria-hidden="true">{mark}</b>}<span>{label}</span>
        </NavLink>
      ))}
    </nav>
  )
}

export function PrimaryShell({ children, title }: { children: ReactNode; title?: string }) {
  return <div className="app-frame app-frame--with-nav"><AppHeader title={title} /><main>{children}</main><BottomNav /></div>
}

export function SecondaryShell({ children, title, backTo }: { children: ReactNode; title: string; backTo?: string }) {
  return <div className="app-frame"><PageHeader title={title} backTo={backTo} /><main>{children}</main></div>
}

export function usePreviewState(): PreviewState {
  const [searchParams] = useSearchParams()
  const state = searchParams.get('state')
  return state === 'loading' || state === 'error' || state === 'empty' ? state : 'ready'
}

export function PreviewRibbon() {
  const location = useLocation()
  return <span className="preview-ribbon" aria-label={`Mock preview，無 production 連線，${location.pathname}`}>Preview</span>
}
